/**
 * Sprint 23.6.1 — AgentProfileMeta.
 *
 * Sidebar Profile section. Three compact rows surfacing the at-a-glance facts
 * that don't fit on the hero strip:
 *
 *   • Style  — agent_config.style (or "—")
 *   • Hired  — created_at, formatted "12 March 2026" (themed label key
 *              `hireDate` — Hired / Joined / Started / Added).
 *   • Model  — model_policy with the explicit model when policy="fixed"
 *              (e.g. "Auto", "Performance", "Fixed · claude-opus-4-8").
 *
 * Rows are display-only here; clicking a row deep-links to the matching
 * subsection of the Settings tab via the `settingsSection` location-state
 * hint that AgentSettingsTab honours.
 */

import { type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";
import type { AgentConfig, LibraryNodeDetail } from "../../../lib/types";
import { useThemedLabels } from "../../hooks/use-themed-labels";

const STYLE_LABELS: Record<string, string> = {
  "matter-of-fact": "Matter-of-fact",
  conversational: "Conversational",
  playful: "Playful",
  formal: "Formal",
};

const POLICY_LABELS: Record<string, string> = {
  auto: "Auto",
  economy: "Economy",
  performance: "Performance",
  fixed: "Fixed",
};

export interface AgentProfileMetaProps {
  node: LibraryNodeDetail;
}

export function AgentProfileMeta({ node }: AgentProfileMetaProps) {
  const labels = useThemedLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const config = (node.agent_config ?? null) as AgentConfig | null;

  const styleValue = config?.style ? STYLE_LABELS[config.style] ?? config.style : "—";
  const hiredValue = formatHireDate(node.created_at);
  const modelValue = formatModel(config);

  function goToSettings(section: "persona" | "model") {
    navigate(
      { pathname: location.pathname, hash: "#settings" },
      { state: { settingsSection: section } },
    );
  }

  return (
    <aside style={wrap}>
      <h3 style={title}>{labels.sidebarProfileLabel}</h3>
      <MetaRow label="Style" value={styleValue} onClick={() => goToSettings("persona")} />
      <MetaRow label={labels.hireDate} value={hiredValue} static />
      <MetaRow label="Model" value={modelValue} onClick={() => goToSettings("model")} />
    </aside>
  );
}

function MetaRow({
  label,
  value,
  onClick,
  static: isStatic,
}: {
  label: string;
  value: string;
  onClick?: () => void;
  static?: boolean;
}) {
  const interactive = !!onClick && !isStatic;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      style={interactive ? rowInteractive : rowStatic}
      aria-label={interactive ? `${label}: ${value} — edit in settings` : `${label}: ${value}`}
    >
      <span style={rowLabel}>{label}</span>
      <span style={rowValue}>{value}</span>
    </button>
  );
}

function formatHireDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatModel(config: AgentConfig | null): string {
  const policy = config?.model_policy ?? "auto";
  const base = POLICY_LABELS[policy] ?? "Auto";
  if (policy === "fixed" && config?.model) {
    return `${base} · ${config.model}`;
  }
  return base;
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: "12px 14px",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-secondary)",
};

const title: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  margin: 0,
};

const rowBase: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
  padding: "4px 0",
  border: "none",
  background: "transparent",
  textAlign: "left",
  fontFamily: "var(--font-sans)",
};

const rowStatic: CSSProperties = {
  ...rowBase,
  cursor: "default",
};

const rowInteractive: CSSProperties = {
  ...rowBase,
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
};

const rowLabel: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const rowValue: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-primary)",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
